import React from "react";

export default function RuntimeKpiGrid({
    overview,
    lang,
    currentLang,
    canSee = () => true
}) {
    const kpi = overview?.kpi || {};
    const isEnglish = currentLang === "en";

    const labels = {
        running:
            lang.dashboard?.runningMachines ||
            (isEnglish ? "Running Machines" : "Çalışan Makine"),

        stopped:
            lang.dashboard?.stoppedMachines ||
            (isEnglish ? "Stopped" : "Durdurulan"),

        fault:
            lang.dashboard?.faultMachines ||
            (isEnglish ? "Fault" : "Arızalı"),

        offline:
            lang.dashboard?.offlineMachines ||
            (isEnglish ? "Offline" : "Çevrimdışı"),

        workOrders:
            lang.dashboard?.activeWorkOrders ||
            (isEnglish ? "Active Work Orders" : "Aktif İş Emri"),

        availability:
            lang.dashboard?.availability ||
            (isEnglish ? "Availability" : "Kullanılabilirlik"),

        performance:
            lang.dashboard?.performance ||
            (isEnglish ? "Performance" : "Performans"),

        quality:
            lang.dashboard?.quality ||
            (isEnglish ? "Quality" : "Kalite"),

        completedToday:
            lang.dashboard?.completedToday ||
            (isEnglish ? "Completed Today" : "Bugün Tamamlanan"),
    };

    const toPercent = value => {
        const number = Number(value);

        return Number.isFinite(number)
            ? `${number.toFixed(1)}%`
            : "0.0%";
    };

    const cards = [
        {
            icon: "🏭",
            permissionKey: "widget_machines",
            label: labels.running,
            value:
                `${kpi.running_machines || 0} / ` +
                `${kpi.total_machines || 0}`,
            color: "#22c55e",
            glow: "rgba(34,197,94,0.16)"
        },
        {
            icon: "⏸",
            permissionKey: "widget_machines",
            label: labels.stopped,
            value: kpi.stopped_machines || 0,
            color: "#f59e0b",
            glow: "rgba(245,158,11,0.15)"
        },
        {
            icon: "!",
            permissionKey: "widget_machines",
            label: labels.fault,
            value: kpi.fault_machines || 0,
            color: "#ef4444",
            glow: "rgba(239,68,68,0.17)"
        },
        {
            icon: "◉",
            permissionKey: "widget_machines",
            label: labels.offline,
            value: kpi.offline_machines || 0,
            color: "#94a3b8",
            glow: "rgba(148,163,184,0.13)"
        },
        {
            icon: "▤",
            permissionKey: "widget_production",
            label: labels.workOrders,
            value: kpi.active_work_orders || 0,
            color: "#60a5fa",
            glow: "rgba(96,165,250,0.16)"
        },
        {
            icon: "A",
            permissionKey: "widget_oee_gauges",
            label: labels.availability,
            value: toPercent(kpi.availability),
            color: "#34d399",
            glow: "rgba(52,211,153,0.16)"
        },
        {
            icon: "P",
            permissionKey: "widget_oee_gauges",
            label: labels.performance,
            value: toPercent(kpi.performance),
            color: "#fbbf24",
            glow: "rgba(251,191,36,0.15)"
        },
        {
            icon: "Q",
            permissionKey: "widget_oee_gauges",
            label: labels.quality,
            value: toPercent(kpi.quality),
            color: "#22d3ee",
            glow: "rgba(34,211,238,0.16)"
        },
        {
            icon: "✓",
            permissionKey: "widget_production",
            label: labels.completedToday,
            value: kpi.completed_today || 0,
            color: "#a78bfa",
            glow: "rgba(167,139,250,0.16)"
        },
    ];

    return (
        <div
            className="opement-runtime-kpi-grid"
            style={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(auto-fit, minmax(108px, 1fr))",
                gap: 12,
                marginTop: 0,
                marginBottom: 14,
            }}
        >
            {cards
                .filter(item => canSee(item.permissionKey))
                .map(item => (
                <div
                    key={item.label}
                    style={{
                        position: "relative",
                        minHeight: 104,
                        padding: "13px 10px 14px",
                        borderRadius: 16,
                        overflow: "hidden",

                        background:
                            "linear-gradient(" +
                            "155deg," +
                            "rgba(255,255,255,0.06) 0%," +
                            "rgba(255,255,255,0.025) 100%" +
                            ")",

                        border:
                            "1px solid rgba(148,163,184,0.09)",

                        boxShadow:
                            `0 10px 28px rgba(2,6,23,0.18), ` +
                            `0 0 22px ${item.glow}, ` +
                            "inset 0 1px 0 rgba(255,255,255,0.04)",

                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",

                        transition:
                            "transform 180ms ease, " +
                            "border-color 180ms ease, " +
                            "box-shadow 180ms ease",
                    }}
                    onMouseEnter={event => {
                        event.currentTarget.style.transform =
                            "translateY(-2px)";

                        event.currentTarget.style.borderColor =
                            "rgba(148,163,184,0.17)";
                    }}
                    onMouseLeave={event => {
                        event.currentTarget.style.transform =
                            "translateY(0)";

                        event.currentTarget.style.borderColor =
                            "rgba(148,163,184,0.09)";
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "18%",
                            right: "18%",
                            height: 2,
                            borderRadius: "0 0 999px 999px",

                            background:
                                `linear-gradient(` +
                                `90deg, transparent, ${item.color}, transparent` +
                                `)`,

                            opacity: 0.7,
                        }}
                    />

                    <div
                        style={{
                            width: 30,
                            height: 30,
                            marginBottom: 9,
                            borderRadius: 9,

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            fontSize: 13,
                            lineHeight: 1,
                            fontWeight: 800,
                            color: item.color,

                            background: item.glow,

                            boxShadow:
                                `inset 0 0 0 1px ${item.glow}`,
                        }}
                    >
                        {item.icon}
                    </div>

                    <div
                        style={{
                            minHeight: 23,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            fontSize: 9,
                            lineHeight: 1.25,
                            letterSpacing: "0.65px",

                            color: "#91a4c1",
                            textTransform: "uppercase",
                            fontWeight: 650,
                        }}
                    >
                        {item.label}
                    </div>

                    <div
                        style={{
                            marginTop: 6,
                            fontSize: 22,
                            lineHeight: 1,
                            fontWeight: 780,
                            letterSpacing: "-0.5px",
                            color: item.color,

                            textShadow:
                                `0 0 18px ${item.glow}`,
                        }}
                    >
                        {item.value}
                    </div>
                </div>
            ))}
        </div>
    );
}
